import React, { type FC, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import { getAgentTrustReceiptActorIcon } from './agentTrustReceiptActorIcons.ts';
import type { AgentTrustReceipt } from './agentTrustReceiptModel.ts';

const i18n = defineMessages({
	actorLabel: {
		id: 'agent-trust-receipt.panel.actor-label.non-final',
		defaultMessage: 'Actor',
		description: 'Label for the actor named in an Agent Trust Receipt.',
	},
	scenarioLabel: {
		id: 'agent-trust-receipt.panel.scenario-label.non-final',
		defaultMessage: 'Scenario',
		description: 'Label for the scenario named in an Agent Trust Receipt.',
	},
	eventsLabel: {
		id: 'agent-trust-receipt.panel.events-label.non-final',
		defaultMessage: 'Events',
		description: 'Heading for the chronological events in an Agent Trust Receipt.',
	},
});

const styles = cssMap({
	container: {
		paddingBlock: token('space.250'),
		paddingInline: token('space.300'),
	},
	section: {
		paddingBlockStart: token('space.150'),
	},
	eventRow: {
		borderBlockStart: `${token('border.width')} solid ${token('color.border')}`,
		paddingBlockStart: token('space.100'),
	},
});

type AgentTrustReceiptPanelBodyProps = {
	receipt: AgentTrustReceipt;
};

export const AgentTrustReceiptPanelBody: FC<AgentTrustReceiptPanelBodyProps> = ({ receipt }) => {
	const { formatDate, formatMessage } = useIntl();
	const ActorIcon = getAgentTrustReceiptActorIcon(receipt.actor.iconKey);
	const chronologicalEvents = useMemo(
		() =>
			[...receipt.events].sort((eventA, eventB) =>
				eventA.occurredAt.localeCompare(eventB.occurredAt),
			),
		[receipt.events],
	);

	return (
		<Box xcss={styles.container} testId="agent-trust-receipt-panel-body">
			<Stack space="space.250">
				<Text>{receipt.summary}</Text>
				<Stack space="space.100" xcss={styles.section}>
					<Inline spread="space-between" alignBlock="start">
						<Text weight="semibold">{formatMessage(i18n.actorLabel)}</Text>
						<Inline space="space.050" alignBlock="center">
							<ActorIcon label="" color="currentColor" />
							<Text>{receipt.actor.name}</Text>
						</Inline>
					</Inline>
					<Inline spread="space-between" alignBlock="start">
						<Text weight="semibold">{formatMessage(i18n.scenarioLabel)}</Text>
						<Text>{receipt.scenario.label}</Text>
					</Inline>
				</Stack>
				<Stack space="space.100" xcss={styles.section}>
					<Text weight="semibold">{formatMessage(i18n.eventsLabel)}</Text>
					{chronologicalEvents.map((event) => (
						<Stack key={event.id} space="space.050" xcss={styles.eventRow}>
							<Text weight="semibold">{event.title}</Text>
							<Text>{event.description}</Text>
							<Text size="small">
								{formatDate(new Date(event.occurredAt), {
									year: 'numeric',
									month: 'short',
									day: 'numeric',
									hour: 'numeric',
									minute: '2-digit',
								})}
							</Text>
						</Stack>
					))}
				</Stack>
			</Stack>
		</Box>
	);
};
